import { Trans, useTranslation } from 'react-i18next'
import { SummarySection } from './SummarySection'
import { PremiumSummaryInfo } from '@cms-max/medicare-api-sdk'
import { MyInternalLink } from 'components/MyLink'
import { Alert } from '@cmsgov/design-system'
import Skeleton from 'react-loading-skeleton'
import { useEvent } from 'hooks/useEvent'

type MyPaymentMethodProps = {
    isLoading?: boolean
    summaryData?: PremiumSummaryInfo
}
export function MyPaymentMethod ({ isLoading, summaryData }: MyPaymentMethodProps) {
  const { t, i18n } = useTranslation('premiumsDashboardPage')
  const { fireEvent } = useEvent('premiumsLanding')
  const lang = i18n.language as 'en' | 'es'
  const beneMessages = summaryData?.beneMessages
  const value = isLoading ? <Skeleton height={75} /> : summaryData?.currentMonthlyPremiumInfo ? <CurrentMonthlyPremium currentMonthlyPremiumInfo={summaryData?.currentMonthlyPremiumInfo} /> : null

  return (
    <SummarySection
        heading={isLoading ? <Skeleton width={237} /> : t('myPaymentMethod.heading')}
        value={value}
        valueColor={isLoading ? undefined : 'gray'}
        isLoading={isLoading}
    >
        {isLoading && <Skeleton height={222} />}
        {!isLoading && (
          <>
            {beneMessages?.howYouPay && <p>{beneMessages?.howYouPay[lang]}</p>}
            {summaryData?.isEasyPayEnrolled && <p><MyInternalLink to="/premiums/easy-pay/edit" onClick={(params) => fireEvent('premiums_landingpage_changeeasypay_click', params)}>{t('myPaymentMethod.changeEasyPayLink')}</MyInternalLink></p>}
            {!summaryData?.isEasyPayEnrolled && summaryData?.isDirectBillingEligible && <EasyPayAlert />}
            {beneMessages?.partAThirdParty && <p>{beneMessages?.partAThirdParty[lang]}</p>}
            {beneMessages?.partBThirdParty && <p>{beneMessages?.partBThirdParty[lang]}</p>}
            <p><MyInternalLink to="/premiums/monthly" onClick={(params) => fireEvent('premiums_landingpage_premiumdetails_click', params)}>{t('myPaymentMethod.premiumDetailsLink')}</MyInternalLink></p>
            <p>{t('myPaymentMethod.moreInfo')}</p>
          </>
        )}
    </SummarySection>
  )
}

export default MyPaymentMethod

type CurrentMonthlyPremiumProps = {
  currentMonthlyPremiumInfo: PremiumSummaryInfo['currentMonthlyPremiumInfo']
}
function CurrentMonthlyPremium ({ currentMonthlyPremiumInfo }: CurrentMonthlyPremiumProps) {
  const { t } = useTranslation('premiumsDashboardPage')
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

  return (
    <div>
      <div className='ds-text-body--md'>{t('myPaymentMethod.currentMonthlyPremium')}</div>
      <div className="ds-text-body--lg ds-u-font-weight--semibold">{formatter.format(currentMonthlyPremiumInfo?.total)}</div>
    </div>
  )
}

// These both link the same place, but leaving these as separate components for now in case we need separate activity tracking
function Link1 ({ children }: {children?: React.ReactNode}) {
  const { fireEvent } = useEvent('premiumsLanding')
  return <MyInternalLink to="/premiums/easy-pay" onClick={(params) => fireEvent('premiums_landingpage_setupeasypay_click', params)}>{children}</MyInternalLink>
}

function Link2 ({ children }: {children?: React.ReactNode}) {
  const { fireEvent } = useEvent('premiumsLanding')
  return <MyInternalLink to="/premiums/easy-pay" onClick={(params) => fireEvent('premiums_landingpage_easypaypage_click', params)}>{children}</MyInternalLink>
}

function EasyPayAlert () {
  const { t } = useTranslation('premiumsDashboardPage')
  return (
    <Alert heading={t('myPaymentMethod.easyPayAlert.heading')} className="ds-u-margin-bottom--2">
      <Trans t={t} i18nKey='myPaymentMethod.easyPayAlert.content' components={{
        link1: <Link1 />,
        link2: <Link2 />
      }} />
    </Alert>
  )
}
